import { CloseOutlined } from '@mui/icons-material';
import { Dialog, Typography, Stack, Box, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ModalConfirmation from '../ModalConfirmation';
import AppointmentForm from 'FRONTEND/components/Calendar/AppointmentForm/AppointmentForm';
import { clearForm, resetBookingForm } from 'FRONTEND/reducers/bookingSlice';
import { goToGivenSlotForm, goToLocationForm, goToGivenExpertForm } from 'FRONTEND/reducers/calendarSlice';
import type { RootState } from 'FRONTEND/reducers/types';
import { selectIsFormStarted } from 'FRONTEND/selectors/bookingSelectors';
import type { User } from 'FRONTEND/utils/backendApi/expert/expert.types';
import { FormTypes } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';

type SwitchableFormType = string;

type AppointmentModalProps = {
  open: boolean;
  onClose: () => void;
  hasAppointmentLocationRights: boolean;
  hasManagementRight: boolean;
  currentUser: User | null;
}


const AppointmentModal = ({
  open = true,
  onClose,
  hasAppointmentLocationRights,
  hasManagementRight,
  currentUser,
}: AppointmentModalProps) => {
  const dispatch = useDispatch();

  const currentFormType = useSelector((state: RootState) => state.booking.formType);
  const isStarted = useSelector(selectIsFormStarted);
  const [targetForm, setTargetForm] = useState<null | SwitchableFormType>(null);

  // Users with management rights search across experts, the others book for
  // themselves. Both alternate with the location form, so the pair on offer
  // depends on the right. The "+" next to a practitioner also lands on
  // GIVEN_EXPERT_FORM, but with an expert already chosen: switching away from it
  // would discard that choice, so the toggle is not offered there.
  const expertFormType = hasManagementRight ? FormTypes.GIVEN_SLOT_FORM : FormTypes.GIVEN_EXPERT_FORM;
  const canSwitchForm = hasAppointmentLocationRights &&
    [expertFormType, FormTypes.LOCATION_FORM].includes(currentFormType);

  const closeAppointmentForm = () => {
    onClose();
  };

  const handleFormSwitch = (clickedForm: SwitchableFormType) => {
    if (!isStarted) {
      handleConfirmation(clickedForm);
    } else {
      setTargetForm(clickedForm);
    }
  };

  const handleConfirmation = (targetForm: SwitchableFormType) => {
    if (targetForm) {
      dispatch(clearForm());
      dispatch(resetBookingForm());
      if (targetForm === FormTypes.LOCATION_FORM && hasAppointmentLocationRights) {
        dispatch(goToLocationForm({}));
      } else if (targetForm === FormTypes.GIVEN_SLOT_FORM) {
        dispatch(goToGivenSlotForm());
      } else {
        dispatch(goToGivenExpertForm({ id: currentUser?.id }));
      }
    }
  };

  const handleModalClose = (isConfirmed: boolean) => {
    if (isConfirmed) {
      handleConfirmation(targetForm);
    }
    setTargetForm(null);
  };

  return (
    <Dialog
      open={open}
      onClose={closeAppointmentForm}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '1200px',
          maxHeight: 800,
          borderRadius: '32px',
          overflow: 'hidden',
        },
      }}
    >
      <Stack spacing={2} alignItems='center' direction='row' justifyContent='space-between' mb={2} p={2}>
        <Box sx={{ width: 40 }} />{/* Spacer for the close button */}
        <Typography variant='h5' fontWeight={600} mb={2}>
          {i18n._('New Appointment')}
        </Typography>
        <Box sx={{ width: 40 }}>
          <IconButton onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        </Box>
      </Stack>
      <Stack alignItems='center' width='100%' sx={{ maxHeight: '100%', overflowY: 'auto', overflowX: 'hidden', px: 2, mb: 1 }}>
        {canSwitchForm && (
          <ToggleButtonGroup
            value={currentFormType as SwitchableFormType}
            size='small'
            exclusive
            sx={{ mb: 2 }}
            onChange={(_event, newValue) => handleFormSwitch(newValue)}
          >
            <ToggleButton value={expertFormType}>
              <Typography variant='body2'>
                {hasManagementRight ? i18n._('Find an expert') : i18n._('Book an appointment')}
              </Typography>
            </ToggleButton>
            <ToggleButton value={FormTypes.LOCATION_FORM}>
              <Typography variant='body2'>
                {i18n._('Find a consultation location')}
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        <AppointmentForm forRightPanel={false} isDialog />
      </Stack>
      <ModalConfirmation
        open={Boolean(targetForm)}
        onClose={handleModalClose}
        title={i18n._('Change Appointment Form')}
        message={i18n._('Are you sure you want to change the appointment form? The fields will be reset.')}
      />
    </Dialog>
  );
};

export default AppointmentModal;
